// The findings flow (lib/reviewFlow.js) read as routes, for the views that
// draw one mark per finding (ReviewFlowConvergence, ReviewFlowCascade): each
// route is the path some findings took from what the reviews spotted to the
// node they stopped at, and how many marks draw it.
//
// A route ends where its node does: a filtered part, not checked yet, cleared,
// not asked yet, or the standard that flagged it first. The routes add up to
// the flow's first node, so a view drawing every route draws every finding.
//
// Past MARKS_MAX findings one mark stands for `unit` findings, so a large
// range costs the same to draw as a small one; the views say so when it does.
// What needs a person keeps a mark per finding up to LIFT_MAX, whatever the
// rest are drawn at: it is the part of the flow a reader acts on.

export const MARKS_MAX = 1200;
export const LIFT_MAX = 48;

// -> { routes: [{ id, kind, stage, node, part?, count, marks, path }], unit, liftUnit, total }.
// `kind`: "drop" (let go at `stage`), "end" (carried to the end) or "lift"
// (needs a person). `path`: the node ids it passes, its own last.
export function flowRoutes(model) {
    if (!model || model.empty) return { routes: [], unit: 1, liftUnit: 1, total: 0 };
    const node = (id) => model.nodes.get(id) || null;
    const count = (id) => node(id)?.count || 0;
    const root = count("written") > 0 ? ["written"] : [];
    const kept = [...root, "kept"];
    const routes = [];
    const add = (r) => { if (r.count > 0) routes.push(r); };

    const filtered = node("filtered");
    if (filtered) {
        const parts = filtered.parts?.length ? filtered.parts : [{ id: "filtered", count: filtered.count }];
        for (const p of parts) add({ id: `filtered:${p.id}`, kind: "drop", stage: "kept", node: "filtered", part: p.id, count: p.count, path: [...root, "filtered"] });
    }
    add({ id: "waiting", kind: "drop", stage: "triage", node: "waiting", count: count("waiting"), path: [...kept, "waiting"] });
    if (count("triaged") > 0) {
        const triaged = [...kept, "triaged"];
        const flags = [...model.nodes.values()].filter((n) => n.flag);
        for (const f of flags) add({ id: f.id, kind: "lift", stage: "standard", node: f.id, count: f.count, path: [...triaged, "met", f.id] });
        // What needs a person with no first standard named still needs a person.
        add({ id: "met", kind: "lift", stage: "standard", node: "met", count: count("met") - flags.reduce((n, f) => n + f.count, 0), path: [...triaged, "met"] });
        add({ id: "cleared", kind: "end", stage: "standard", node: "cleared", count: count("cleared"), path: [...triaged, "cleared"] });
        add({ id: "not_asked", kind: "drop", stage: "standard", node: "not_asked", count: count("not_asked"), path: [...triaged, "not_asked"] });
    } else if (count("waiting") === 0) {
        // Triage unread: the kept findings are where the flow can follow them to.
        add({ id: "kept", kind: "end", stage: "kept", node: "kept", count: count("kept"), path: kept });
    }
    const total = routes.reduce((n, r) => n + r.count, 0);
    const unit = Math.max(1, Math.ceil(total / MARKS_MAX));
    const lifted = routes.reduce((n, r) => n + (r.kind === "lift" ? r.count : 0), 0);
    const liftUnit = Math.max(1, Math.ceil(lifted / LIFT_MAX));
    for (const r of routes) r.marks = Math.max(1, Math.round(r.count / (r.kind === "lift" ? liftUnit : unit)));
    return { routes, unit, liftUnit, total };
}

// Every route's marks spread evenly through one sequence, so where a mark
// starts says nothing about where it ends until the flow sorts it.
export function interleave(routes) {
    const marks = [];
    routes.forEach((route, ri) => {
        for (let j = 0; j < route.marks; j++) marks.push({ route, j, key: (j + 0.5) / route.marks + ri * 1e-6 });
    });
    return marks.sort((a, b) => a.key - b.key);
}
